import { useCallback, useEffect, useState } from 'react';

import {
  useApiMutation,
  useApiQuery,
  useInvalidateQueries,
} from '@/shared/hooks/useApi';
import { api } from '@/shared/services/api';
import { ApiResponse } from '@/shared/types/apiTypes';

import {
  CreateKeywordRequest,
  KeywordGroupDetail,
  UpdateKeywordRequest,
  UseKeywordManageReturn,
} from '../types/keywordTypes';

const fetchKeywordDetail = async (
  keywordGroupId: number,
): Promise<ApiResponse<KeywordGroupDetail>> => {
  return api.get<KeywordGroupDetail>(
    `/api/v1/keyword/detail/${keywordGroupId}`,
  );
};

const createKeyword = async (
  data: CreateKeywordRequest,
): Promise<ApiResponse<KeywordGroupDetail>> => {
  return api.post<KeywordGroupDetail>('/api/v1/keyword', data);
};

const updateKeyword = async (
  keywordGroupId: number,
  data: UpdateKeywordRequest,
): Promise<ApiResponse<string>> => {
  return api.patch<string>(`/api/v1/keyword/${keywordGroupId}`, data);
};

const deleteKeyword = async (
  keywordGroupId: number,
): Promise<ApiResponse<string>> => {
  return api.delete<string>(`/api/v1/keyword/${keywordGroupId}`);
};

export const useKeywordManage = (
  keywordGroupId: number | null,
  onSuccess: () => void,
): UseKeywordManageReturn => {
  const [groupName, setGroupName] = useState('');
  const [keywordList, setKeywordList] = useState<string[]>(['']);

  const { invalidateQueries } = useInvalidateQueries();
  const isEdit = keywordGroupId !== null;

  // keywordGroupId가 변경될 때마다 즉시 상태 초기화
  useEffect(() => {
    if (!isEdit) {
      setGroupName('');
      setKeywordList(['']);
    }
  }, [keywordGroupId]);

  const {
    data: keywordDetailData,
    isLoading,
    error: detailError,
    refetch: refetchDetail,
  } = useApiQuery(
    ['keywordDetail', keywordGroupId ?? 'new'],
    () => fetchKeywordDetail(keywordGroupId!),
    {
      enabled: isEdit,
      refetchOnMount: 'always',
      staleTime: 0,
      gcTime: 0,
    },
  );

  // 생성 후 처리 - 성공 시 폼 즉시 초기화
  const createMutation = useApiMutation(createKeyword, {
    onSuccess: (data) => {
      // 폼을 먼저 초기화
      setGroupName('');
      setKeywordList(['']);

      alert('Keyword group created successfully.');
      invalidateQueries(['keywordList']);
      onSuccess();
    },
    onError: (error: any) => {
      alert(error.message || 'Failed to create keyword group.');
    },
  });

  // 수정 후 처리
  const updateMutation = useApiMutation(
    ({ id, data }: { id: number; data: UpdateKeywordRequest }) =>
      updateKeyword(id, data),
    {
      onSuccess: () => {
        alert('Keyword group updated successfully.');
        invalidateQueries(['keywordList']);
        if (keywordGroupId !== null) {
          invalidateQueries(['keywordDetail', keywordGroupId]);
        }
        onSuccess();
      },
      onError: (error: any) => {
        alert(error.message || 'Failed to update keyword group.');
      },
    },
  );

  // 삭제 후 처리
  const deleteMutation = useApiMutation(deleteKeyword, {
    onSuccess: () => {
      alert('Keyword group deleted successfully.');
      invalidateQueries(['keywordList']);
      if (keywordGroupId !== null) {
        invalidateQueries(['keywordDetail', keywordGroupId]);
      }
      onSuccess();
    },
    onError: (error: any) => {
      alert(error.message || 'Failed to delete keyword group.');
    },
  });

  // 수정 모드일 때만 데이터를 폼에 설정
  useEffect(() => {
    if (keywordDetailData?.data && isEdit) {
      const detail = keywordDetailData.data;
      setGroupName(detail.group);
      const keywords = detail.keywords.map((k) => k.keyword);
      setKeywordList(keywords.length > 0 ? keywords : ['']);
    }
  }, [keywordDetailData, isEdit]);

  // 에러 처리
  useEffect(() => {
    if (detailError) {
      alert(detailError.message || 'Failed to load keyword group information.');
    }
  }, [detailError]);

  const handleGroupNameChange = useCallback((value: string) => {
    setGroupName(value);
  }, []);

  const handleKeywordChange = useCallback((index: number, value: string) => {
    setKeywordList((prev) => {
      const newList = [...prev];
      newList[index] = value;
      return newList;
    });
  }, []);

  const handleAddKeyword = useCallback(() => {
    setKeywordList((prev) => [...prev, '']);
  }, []);

  const handleRemoveKeyword = useCallback((index: number) => {
    setKeywordList((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const canDeleteKeyword = useCallback(
    (index: number) => {
      const keyword = keywordList[index];
      const trimmedGroupName = groupName.trim();
      const trimmedKeyword = keyword.trim();

      if (keywordList.length <= 1) return false;
      if (trimmedGroupName && trimmedKeyword === trimmedGroupName) return false;

      return true;
    },
    [keywordList, groupName],
  );

  const handleSave = useCallback(async () => {
    const trimmedGroupName = groupName.trim();
    const trimmedKeywords = keywordList
      .map((k) => k.trim())
      .filter((k) => k.length > 0);

    if (!trimmedGroupName) {
      alert('Please enter a keyword group name.');
      return;
    }

    if (trimmedKeywords.length === 0) {
      alert('Please enter at least 1 keyword.');
      return;
    }

    if (!trimmedKeywords.includes(trimmedGroupName)) {
      alert(
        'The keyword list must include a keyword that is the same as the group name.',
      );
      return;
    }

    const requestData = {
      groupName: trimmedGroupName,
      keywordList: trimmedKeywords,
    };

    if (isEdit && keywordGroupId) {
      updateMutation.mutate({ id: keywordGroupId, data: requestData });
    } else {
      createMutation.mutate(requestData);
    }
  }, [
    groupName,
    keywordList,
    isEdit,
    keywordGroupId,
    createMutation,
    updateMutation,
  ]);

  const handleDelete = useCallback(async () => {
    if (!isEdit || !keywordGroupId) return;

    if (window.confirm('Are you sure you want to delete this keyword group?')) {
      deleteMutation.mutate(keywordGroupId);
    }
  }, [isEdit, keywordGroupId, deleteMutation]);

  const canSave =
    groupName.trim().length > 0 &&
    keywordList.some((k) => k.trim().length > 0) &&
    !createMutation.isPending &&
    !updateMutation.isPending;

  return {
    groupName,
    keywordList,
    isLoading,
    isSaving: createMutation.isPending || updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    hasError: !!detailError,
    error: detailError,
    handleGroupNameChange,
    handleKeywordChange,
    handleAddKeyword,
    handleRemoveKeyword,
    handleSave,
    handleDelete,
    canDeleteKeyword,
    canSave,
  };
};
